import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import MainLayout from "../../Shared/Layout/MainLayout"
import ModalGroup from "../../Shared/Components/ModalGroup"
import Header from "../../Shared/Header/Header"
import Footer from "../../Shared/Layout/Footer"
import DonateButton from "../../Shared/Components/DonateButton"
import RoomDropdown from "../../Shared/Components/RoomDropdown"
import NewestMembersCard from "../../Shared/NewestMembers/NewestMemberCard"
import SponsorCard from "../../Shared/Components/SponsorCard"
import { RoleType } from "../../../App/enums"
import OrganizationProfileCard from "../../Shared/Components/OrganizationProfileCard"
import { useLocation, useNavigate } from "react-router"
import { RootState } from "../../../App/store"
import BackgroundWrapper from "../../Shared/Components/BackgroundWrapper"
import NewestOrganizationsCard from "../../Shared/NewestOrganizations/NewestOrganizations"
import { EdusiiaSmallIcon, LoadingAnimation, SearchIcon } from "../../Shared/Components/SvgIcons"
import { not_found } from "../../../App/GlobaleVariables"
import ChatList from "./ChatList"
import ChatHeader from "../Components/ChatHeader"
import { ConversationMessageSeenPostRequest, CreateConversationPostRequest, chatGetByPartnerIdRequest, chatListGetRequest, singleChatListGetRequest } from "../ChatServiceRequest"
import { setActiveConversation, setConversationList, setHasMoreConversations } from "../ChatSlice"
import { ConversationMessage } from "../../../Types/Conversation"
import ChatContent from "../Components/ChatContent"
import ChatTextbox from "../Components/ChatTextbox"
import HTTPService from "../../../App/HTTPService"
import { headers } from "../../../App/Axios"
import SearchInput from "../../Shared/Primitive/SearchInput"
import { GlobalSearchResult } from "../../../Types/GlobalSearchResultType"
import Image from "../../Shared/Primitive/Image"
import { useAppSocket } from "../../Shared/Socket/AppSocketProvider"

const pickFirstString = (...values: Array<unknown>) => {
    for (const value of values) {
        if (typeof value === "string") {
            return value
        }
    }

    return ""
}

const normalizeIncomingMessage = (rawMessage: any): ConversationMessage => {
    const normalizedContent = pickFirstString(
        rawMessage?.content,
        rawMessage?.formatted_content,
        rawMessage?.formattedContent,
        rawMessage?.html_content,
        rawMessage?.htmlContent,
        rawMessage?.message,
        rawMessage?.text,
    )

    return {
        ...rawMessage,
        content: normalizedContent,
    }
}

function UserChatScreen() {
    const translation = useSelector(selectTranslations)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const donationLink = useSelector((state: RootState) => state.UserSettingsStatusSlice.userDonationLinkSlice.link)
    const activeConversation = useSelector((state: RootState) => state.chatSlice.activeConversationId)
    const conversations = useSelector((state: RootState) => state.chatSlice.conversations)
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(true)
    const [isSearching, setIsSearching] = useState(false)
    const [sentMessage, SetsentMessage] = useState<ConversationMessage>()
    const [editedMessage, setEditedMessage] = useState<ConversationMessage>()
    const [messageToEdit, setMessageToEdit] = useState<ConversationMessage>()
    const [searchInputValue, setSearchInputValue] = useState("")
    const [searchedUsers, setSearchedUsers] = useState<GlobalSearchResult[]>([])
    const activeConversationRef = useRef(activeConversation)
    const conversationsRef = useRef(conversations)
    const { isConnected, sendJson, addMessageListener } = useAppSocket()

    const searchUsersGetRequest = (searchQuery: string) => {
        return HTTPService.get(`/search/?query=${searchQuery}&page=1&result_type=users`, headers)
    }

    useEffect(() => {
        activeConversationRef.current = activeConversation
        setMessageToEdit(undefined)
    }, [activeConversation])

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const rawConversationId = params.get("conversation")
        if (!rawConversationId) {
            return
        }

        const requestedConversationId = Number(rawConversationId)
        if (!Number.isInteger(requestedConversationId) || requestedConversationId < 1) {
            return
        }

        const conversationExists = conversations.some((conversation) => conversation.id === requestedConversationId)
        if (!conversationExists) {
            singleChatListGetRequest(requestedConversationId)
                .then((response) => {
                    if (response.status !== 200) {
                        return
                    }

                    const updatedConversations = [response.data, ...conversations]
                        .filter((conversation, index, allConversations) => {
                            return allConversations.findIndex((item) => item.id === conversation.id) === index
                        })
                        .sort((a, b) => b.updated_at - a.updated_at)

                    dispatch(setConversationList(updatedConversations))
                    dispatch(setActiveConversation(requestedConversationId))
                })
                .catch((error) => {
                    console.log(error)
                })
            return
        }

        if (activeConversation !== requestedConversationId) {
            dispatch(setActiveConversation(requestedConversationId))
        }
    }, [activeConversation, conversations, dispatch, location.search])

    useEffect(() => {
        conversationsRef.current = conversations
    }, [conversations])

    useEffect(() => {
        const unsubscribe = addMessageListener((data) => {
            switch (data.msg_type) {
                case "new.message": {
                    const normalizedMessage = normalizeIncomingMessage(data.data)
                    const conv_id = normalizedMessage.conversation.id
                    const currentActiveConversation = activeConversationRef.current
                    const currentConversations = conversationsRef.current

                    if (conv_id === currentActiveConversation) {
                        SetsentMessage(normalizedMessage)
                        const sent = sendJson({ type: "msg.seen", messages: [normalizedMessage.id] })
                        if (!sent) {
                            ConversationMessageSeenPostRequest(conv_id, [normalizedMessage.id]).catch((error) => {
                                console.log(error)
                            })
                        }

                        const activeConversationItem = currentConversations.find((item) => item.id === conv_id)
                        if (activeConversationItem && activeConversationItem.unseen_messages > 0) {
                            const updatedConversations = currentConversations.map((item) => {
                                if (item.id === conv_id) {
                                    return {
                                        ...item,
                                        unseen_messages: 0,
                                    }
                                }

                                return item
                            })

                            dispatch(setConversationList(updatedConversations))
                        }
                    } else {
                        const hasConversationInList = currentConversations.some((item) => item.id === conv_id)

                        if (hasConversationInList) {
                            const newList = currentConversations.map((item) => {
                                if (item.id === conv_id) {
                                    return {
                                        ...item,
                                        unseen_messages: (item.unseen_messages || 0) + 1,
                                    }
                                }

                                return item
                            })

                            dispatch(setConversationList(newList))
                        } else {
                            singleChatListGetRequest(conv_id)
                                .then((response) => {
                                    if (response.status !== 200) {
                                        return
                                    }

                                    const serverConversation = response.data
                                    const upsertedConversation = {
                                        ...serverConversation,
                                        unseen_messages: Math.max(1, serverConversation.unseen_messages || 0),
                                    }

                                    const updatedConversations = [upsertedConversation, ...conversationsRef.current]
                                        .filter((conversation, index, allConversations) => {
                                            return allConversations.findIndex((item) => item.id === conversation.id) === index
                                        })
                                        .sort((a, b) => b.updated_at - a.updated_at)

                                    dispatch(setConversationList(updatedConversations))
                                })
                                .catch((error) => {
                                    console.log(error)
                                })
                        }
                    }

                    break
                }
                case "edit.message": {
                    const normalizedMessage = normalizeIncomingMessage(data.data)
                    const conv_id = normalizedMessage.conversation.id
                    const currentActiveConversation = activeConversationRef.current

                    if (conv_id === currentActiveConversation) {
                        setEditedMessage({
                            ...normalizedMessage,
                            is_edited: normalizedMessage.is_edited ?? true,
                        })
                    }

                    break
                }
                default:
                    break
            }
        })

        return () => {
            unsubscribe()
        }
    }, [addMessageListener, dispatch, sendJson])

    const onRemoveIconClick = () => {
        setSearchInputValue("")
        setIsSearching(false)
    }

    useEffect(() => {
        const getData = setTimeout(() => {
            if (searchInputValue.length > 2) {
                setIsSearching(true)
                searchUsersGetRequest(searchInputValue).then(response => {
                    if (response.status === 200) {
                        setSearchedUsers(response.data.results)
                    }
                })
            } else {
                setIsSearching(false)
            }
        }, 500)
        return () => clearTimeout(getData)
    }, [searchInputValue])

    useEffect(() => {

        chatListGetRequest().then(response => {
            if (response.status === 200) {
                dispatch(setConversationList(response.data.results))
                dispatch(setHasMoreConversations(Boolean(response.data.next)))
            }
        })
        setIsLoading(false)
    }, [dispatch])

    const onChatClick = (userId: number) => {
        //Check if conversation with user exists
        let selectedConversation = null
        selectedConversation = conversations.find(conv => conv.member.some(member => member.user.id === userId))?.id || null

        //Test if conversation exists on the server
        if (selectedConversation === null) {

            chatGetByPartnerIdRequest(userId).then(response => {
                if (response.status === 200) {
                    dispatch(setConversationList([response.data].concat(conversations).sort((a: { updated_at: number }, b: { updated_at: number }) => b.updated_at - a.updated_at)))
                    dispatch(setActiveConversation(response.data.id))
                }
            }).catch(err => {
                if (err.response.status === 404) {
                    //Create new conversation
                    try {
                        CreateConversationPostRequest(userId, userDataState.id).then(conv => {
                            setIsSearching(false)
                            setSearchInputValue("")

                            if (conv.status === 201) {
                                singleChatListGetRequest(conv.data.id).then(response => {
                                    let convList = [...conversations, response.data]
                                    convList = convList.sort((a, b) => b.updated_at - a.updated_at)
                                    dispatch(setConversationList(convList))
                                    dispatch(setActiveConversation(conv.data.id))
                                })
                            }
                        })
                    } catch (e) {
                        console.log("ERROR CREATING NEW CONVERSATION")
                        console.log(e)
                    }
                }

            }
            )
        }
    }

    const onMessageSent = (message: ConversationMessage) => {
        SetsentMessage(message)
        let newList = conversations.map((item) => {
            if (item.id === message.conversation.id) {
                const updatedItem = {
                    ...item,
                    updated_at: Date.now(),
                };

                return updatedItem;
            }

            return item;
        })
        newList = newList.sort((a, b) => b.updated_at - a.updated_at)

        dispatch(setConversationList(newList))
    }

    const onMessageEdited = (message: ConversationMessage) => {
        setEditedMessage({
            ...message,
            is_edited: message.is_edited ?? true,
        })
        setMessageToEdit(undefined)
    }

    const onStartEditMessage = (message: ConversationMessage) => {
        setMessageToEdit(message)
    }

    const onCancelEditMessage = () => {
        setMessageToEdit(undefined)
    }

    const onSeeAllEventsClick = () => {
        navigate(`/all-premium-events`)
    }

    return (
        <BackgroundWrapper>
            <Header />
            <MainLayout
                leftPanelFirstComponent={<OrganizationProfileCard />}
                leftPanelSecondComponent={<RoomDropdown type={RoleType.USER} />}
                leftPanelForthComponent={translation.newestOrganizations}
                leftPanelFifthComponent={<NewestOrganizationsCard />}
                RightPanelFirstComponent={donationLink === not_found ? <DonateButton /> : null}
                RightPanelSecondComponent={translation.newsMembersText}
                RightPanelThirdComponent={<NewestMembersCard />}
                RightPanelForthComponent={
                    <div className="main-layout__sponsored-title">
                        {translation.sponsoredText}
                        <div onClick={onSeeAllEventsClick} className="main-layout__sponsored-icon">
                            <SearchIcon color="dark-blue" />
                        </div>
                    </div>
                }
                RightPanelFifthComponent={<SponsorCard />}
                overflowModifier={true}
                isDashboad={false}
            >
                <div className="chat-screen">
                    {isLoading && <LoadingAnimation />}
                    <div className="chat-layout">
                        <div className={`chat-layout__left-panel ${activeConversation === 0 && "chat-layout__left-panel--visible"}`}>
                            <div className="chat-layout__left-panel-content">
                                <div className="chat-header-text">
                                    {translation.directChat}
                                </div>
                                <div className="chat-layout__search-container-outer">
                                    <div className="chat-layout__search-container">
                                        <SearchInput
                                            onRemoveIconClick={onRemoveIconClick}
                                            searchvalue={searchInputValue!}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setSearchInputValue(event.target.value)
                                            }}
                                            isGlobalSearch={false}
                                            isChatSearch={true}
                                        />
                                    </div>
                                </div>
                                {isSearching ? (
                                    <div className={`chat-layout__chatlist card ${searchedUsers.length === 0 && "chat-layout__center"} `}>
                                        <div className="chat-card__members">
                                            {searchedUsers.length === 0 && (
                                                <div className="chat-card-item chat-card-item--no-border">
                                                    <div className="chat-card-item">
                                                        <div className="chat-card-item__middle-panel">
                                                            <div className="chat-card-item__no-search-results">Keine Treffer</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {searchedUsers.map((item, index) => {
                                                return (
                                                    <div className="chat-card-item chat-card-item--no-border" onClick={() => onChatClick(item.id)}>
                                                        <div className="chat-card-item">
                                                            <div className="chat-card-item__left-panel">
                                                                {item.profile_photo?.url_path ? (
                                                                    <Image
                                                                        src={item.profile_photo?.url_path}
                                                                        className="chat-card-item__image image__profile image__profile--fit image__profile--size-small"
                                                                    />
                                                                ) : (
                                                                    <Image
                                                                        src="/edusiia_placeholder_individual_profile.png"
                                                                        className="chat-card-item__image image__profile image__profile--fit image__profile--size-small"
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="chat-card-item__middle-panel">
                                                                <div className="chat-card-item__text">
                                                                    {item.first_name} {item.last_name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <ChatList />
                                )}
                            </div>
                        </div>
                        <div className={`chat-layout__right-panel ${activeConversation === 0 && "chat-layout__right-panel--invisible"}`}>
                            {activeConversation === 0 && (
                                <div className="chat-layout__empty-state" role="status" aria-live="polite">
                                    <div className="chat-layout__empty-state-icon" aria-hidden="true">
                                        <EdusiiaSmallIcon />
                                    </div>
                                    <div className="chat-layout__empty-state-title">{translation.chatEmptyTitle}</div>
                                    <div className="chat-layout__empty-state-text">{translation.chatEmptyText}</div>
                                    <div className="chat-layout__empty-state-cta">{translation.chatEmptyCta}</div>
                                </div>
                            )}
                            {activeConversation > 0 && (
                                <>
                                    <ChatHeader />

                                    <ChatContent
                                        sentMsg={sentMessage}
                                        editedMsg={editedMessage}
                                        onStartEdit={onStartEditMessage}
                                    />

                                    {messageToEdit && (
                                        <div className="chat-layout__edit-mode-notice chat-layout__edit-mode-notice--between-content-and-input">
                                            <div className="chat-layout__edit-mode-text">{translation.editingMessage}</div>
                                            <div className="chat-layout__edit-mode-cancel" onClick={onCancelEditMessage}>{translation.cancel}</div>
                                        </div>
                                    )}

                                    <ChatTextbox
                                        onSendMessage={onMessageSent}
                                        onMessageEdited={onMessageEdited}
                                        editingMessage={messageToEdit}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </MainLayout>
            <Footer />
            <ModalGroup />
        </BackgroundWrapper>
    )
}

export default UserChatScreen
